import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';

const TONES = {
  default: 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
  danger: 'border-red-200 bg-white text-red-700 hover:bg-red-50',
  primary: 'border-brand-300 bg-brand-50 text-brand-800 hover:bg-brand-100',
} as const;

/**
 * Dimensions per size. `xs` is the profile-toolbar size — a record-level action sits in a
 * one-row strip under the header, where reference §14's 56px chip is taller than the strip
 * it lives in. It stops at 40px rather than going smaller, which is the touch-target floor
 * §5 sets for every control.
 *
 * **The HEIGHT is the ramp; the WIDTH is the content's.** A chip carrying an abbreviation is a
 * pill that grows to fit it, and only an icon-only chip keeps the square — a fixed width would
 * force the text to wrap back under the icon, which is the stacked shape this moved away from.
 */
const SIZES = {
  xs: { frame: 'h-10 text-[10px]', pad: 'gap-1.5 px-2.5', square: 'w-10' },
  sm: { frame: 'h-14 text-[11px]', pad: 'gap-2 px-3.5', square: 'w-14' },
  lg: { frame: 'h-16 text-xs', pad: 'gap-2 px-4', square: 'w-16' },
} as const;

/**
 * Compact profile-toolbar chip — reference §14, laid out HORIZONTALLY: the icon on the left,
 * its abbreviation on the right.
 *
 * **Product call, 22 Sep 2026**, and a deviation from §14's icon-OVER-abbreviation square.
 * Stacked, the chip spent its width on three letters set at 10px and its height on a second
 * line to hold them; side by side the glyph and the word read as one control, and it is the
 * axis the row of chips already runs along. The order is the page's own reading direction —
 * the glyph identifies, the word names.
 *
 * Always wrapped in `<Tooltip>`, never bare: the abbreviation alone is not self-explanatory.
 * Disable it only via `disabledReason`, so a disabled chip always says why.
 *
 * **`abbr` is OPTIONAL, and omitting it gives an icon-only chip** — product call, 18 Sep 2026,
 * which is a deviation from §14's abbreviation and is in the deviations table. What
 * makes it safe is the two things this component already enforces and does not relax: `label`
 * is still required and is still the accessible name, and the `<Tooltip>` is still mandatory.
 * So an icon-only chip is named to a screen reader and explains itself on hover and focus —
 * which is the whole job the abbreviation was doing for a sighted reader, and the reason a
 * bare icon button is normally refused.
 */
export function ShortcutButton({
  icon,
  abbr,
  label,
  tooltip,
  disabledReason = null,
  tone = 'default',
  size = 'sm',
  badge = null,
  onClick,
  id,
}: {
  icon: ReactNode;
  /** Omit for an icon-only chip. Decorative when present — `label` is the accessible name. */
  abbr?: string;
  /** Accessible name, and the ONLY one: the abbreviation is decorative and may be absent. */
  label: string;
  tooltip: string;
  disabledReason?: string | null;
  tone?: keyof typeof TONES;
  /** `xs` for a profile toolbar; `sm` and `lg` are reference §14's two steps. */
  size?: keyof typeof SIZES;
  /** Pill renders only when `count > 0`; amber while pending. */
  badge?: { count: number; pending: boolean } | null;
  onClick: () => void;
  id?: string;
}) {
  const disabled = Boolean(disabledReason);
  const { frame, pad, square } = SIZES[size];

  return (
    <Tooltip label={tooltip} disabledReason={disabledReason}>
      <button
        type="button"
        id={id}
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={[
          'relative inline-flex shrink-0 items-center justify-center rounded-panel border transition',
          frame,
          /*
            A chip with an abbreviation is a PILL — padded, and as wide as what is in it. One
            without keeps the square it always had: there is nothing beside the glyph to make
            room for, and a padded box round a lone icon reads as a button missing its label.
          */
          abbr ? pad : square,
          disabled ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300' : TONES[tone],
        ].join(' ')}
      >
        <span aria-hidden="true">{icon}</span>
        {abbr ? (
          /* The frame is a fixed height, so a wrapped abbreviation would break the row. */
          <span className="whitespace-nowrap font-medium uppercase tracking-wide">{abbr}</span>
        ) : null}

        {badge && badge.count > 0 ? (
          <span
            className={[
              'absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full px-1 font-mono tabular-nums text-[10px] text-white',
              badge.pending ? 'bg-amber-500' : 'bg-blue-500',
            ].join(' ')}
          >
            {badge.count}
          </span>
        ) : null}
      </button>
    </Tooltip>
  );
}
